import Route from "@/models/Route";
import generateId from "@/helpers/generateId";
import api from "@/api";

const state = {
  routes: [],
  activeRouteId: null
}

const getters = {
  activeRoute: (state) => state.routes.find(({ id }) => id === state.activeRouteId) || null,
  isCoordinateInRoute: (state) => (routeId, coordinateId) => {
    const route = state.routes.find(({ id }) => id === routeId)

    return route?.coordinatesIds?.includes(coordinateId) || false
  },
  getRouteById: (state) => (routeId) => state.routes.find(({ id }) => id === routeId) || null,
  isCoordinateInActiveRoute: (state, getters) => (coordinateId) => {
    return getters.activeRoute
      ? getters.isCoordinateInRoute(getters.activeRoute.id, coordinateId)
      : false
  }
}

const mutations = {
  SET_ROUTES(state, routes) {
    state.routes = routes
  },
  PUSH_ROUTE(state, route) {
    state.routes.push(route)
  },
  REMOVE_ROUTE(state, routeId) {
    state.routes = state.routes.filter(({ id }) => id !== routeId)
  },
  SET_ACTIVE_ROUTE_ID(state, routeId) {
    state.activeRouteId = routeId
  },
  ADD_COORDINATE_TO_ROUTE(state, { routeId, coordinateId }) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.addCoordinate(coordinateId)
    }
  },
  REMOVE_COORDINATE_FROM_ROUTE(state, { routeId, coordinateId }) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.removeCoordinate(coordinateId)
    }
  },
  SET_ROUTE_COORDINATES_IDS(state, { routeId, coordinatesIds, isReset }) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.setCoordinatesIds(coordinatesIds, isReset)
    }
  },
  START_ROUTE_EDITING(state, routeId) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.startEditing()
    }
  },
  SAVE_ROUTE_EDITING(state, routeId) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.saveEditing()
    }
  },
  CANCEL_ROUTE_EDITING(state, routeId) {
    const route = state.routes.find(({ id }) => id === routeId)

    if (route) {
      route.cancelEditing()
    }
  }
}

const actions = {
  async initRoutes({ commit }) {
    const { data: { data: apiRoutes } } = await api.getRoutes()
    const savedRoutes = JSON.parse(localStorage.getItem('customRoutes') || '[]')

    const routes = apiRoutes
      .map((data) => new Route(Object.assign({}, data, savedRoutes.find(({ id }) => id === data.id) || undefined)))
      .sort(({ order: orderA }, { order: orderB }) => orderB - orderA)

    const newRoute = savedRoutes.find(({ isNew }) => isNew)

    if (newRoute) {
      routes.push(new Route(newRoute))
    }

    commit('SET_ROUTES', routes)
  },
  removeRoute({ dispatch, commit }, routeId) {
    commit('REMOVE_ROUTE', routeId)
    dispatch('saveCustomRoutes')
  },
  saveCustomRoutes({ state }) {
    const routesToSave = state.routes
      .filter(({ isCustom, isNew, isValid }) => (isCustom || isNew) && isValid)
      .map((route) => {
        return {
          id: route.id,
          coordinatesIds: route.coordinatesIds,
          isCustom: route.isCustom,
          isNew: route.isNew
        }
      })

    localStorage.setItem('customRoutes', JSON.stringify(routesToSave))
  },
  startNewRoute({ commit, dispatch }) {
    const newRoute = new Route({
      id: generateId(),
      isNew: true
    })

    newRoute.startEditing()

    commit('PUSH_ROUTE', newRoute)
    dispatch('setActiveRouteId', newRoute.id)
  },
  addCoordinateToRoute({ dispatch, commit }, { routeId, coordinateId }) {
    commit('ADD_COORDINATE_TO_ROUTE', { routeId, coordinateId })
    dispatch('saveCustomRoutes')
  },
  removeCoordinateFromRoute({ dispatch, commit }, { routeId, coordinateId }) {
    commit('REMOVE_COORDINATE_FROM_ROUTE', { routeId, coordinateId })
    dispatch('saveCustomRoutes')
  },
  setRouteCoordinatesIds({ dispatch, commit }, { routeId, coordinatesIds }) {
    commit('SET_ROUTE_COORDINATES_IDS', { routeId, coordinatesIds })
    dispatch('saveCustomRoutes')
  },
  async resetRouteToApi({ dispatch, commit }, routeId) {
    const { data: { data: apiRoutes } } = await api.getRoutes()
    const currentApiRoute = apiRoutes?.find(({ id }) => id === routeId)

    if (currentApiRoute) {
      commit('SET_ROUTE_COORDINATES_IDS', {
        routeId,
        coordinatesIds: currentApiRoute.points?.map((point) => point.coordinate.id) || [],
        isReset: true
      })

      dispatch('saveCustomRoutes')
    }
  },
  setActiveRouteId({ getters, commit, dispatch }, routeId) {
    if (getters.activeRoute?.isEditing) {
      dispatch('cancelRouteEditing', getters.activeRoute.id)
    }

    commit('SET_ACTIVE_ROUTE_ID', routeId)
  },
  startRouteEditing({ commit }, routeId) {
    commit('START_ROUTE_EDITING', routeId)
  },
  saveRouteEditing({ dispatch, commit }, routeId) {
    commit('SAVE_ROUTE_EDITING', routeId)
    dispatch('saveCustomRoutes')
  },
  cancelRouteEditing({ dispatch, commit, getters }, routeId) {
    commit('CANCEL_ROUTE_EDITING', routeId)

    if (!getters.getRouteById(routeId)?.isValid) {
      dispatch('removeRoute', routeId)
    }

    dispatch('saveCustomRoutes')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}